import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowLeftRight, Eye, EyeOff, GripVertical, Plus, Trash2 } from "lucide-react";
import { rhm, apiError } from "../api";
import { Badge, Btn, Card, Empty, Field, Input, Modal, PageHead, Select, Textarea } from "../ui";
import { useRhmAuth } from "../RhmAuthContext";
import { TILE_LABELS } from "../../components/dashboard/DashTiles";

const SEGMENT_LABELS = {
    consultation_buyers: "Consultation buyers",
    ebook_buyers: "E-book buyers",
    product_buyers: "Buyers of a product",
    tag: "Customers with a tag",
};
const NEW_TILE_TYPES = ["orders_purchases", "orders_pending", "guides", "consultations", "blog",
    "products", "books", "image", "review", "text", "button", "reorders", "profile",
    "free_consult", "first_order_offer"];

const newTile = (type) => ({
    id: `tile_${Math.random().toString(36).slice(2, 12)}`, type, visible: true,
    heading: TILE_LABELS[type] || "", body: "", bg: "", heading_color: "", body_color: "",
    image_url: "", link: "", product_ids: [], limit: 3,
    button: { label: "", href: "", bg: "", color: "" },
});

export default function RhmDashboardLayout() {
    const { can } = useRhmAuth();
    const qc = useQueryClient();
    const editable = can("website.edit");
    const [activeId, setActiveId] = useState("");
    const [zones, setZones] = useState({ main: [], sidebar: [] });
    const [dirty, setDirty] = useState(false);
    const [editing, setEditing] = useState(null);
    const [adding, setAdding] = useState(false);
    const [creating, setCreating] = useState(false);

    const { data, isLoading } = useQuery({
        queryKey: ["dash-layouts"],
        queryFn: async () => (await rhm.get("/dashlayout/layouts")).data,
    });
    const { data: products = [] } = useQuery({
        queryKey: ["rhm-products"],
        queryFn: async () => (await rhm.get("/products")).data,
    });

    const layouts = data?.layouts || [];
    const active = useMemo(
        () => layouts.find((l) => l.id === activeId) || layouts.find((l) => l.kind === "default"),
        [layouts, activeId]);

    useEffect(() => {
        if (active) {
            setActiveId(active.id);
            setZones({ main: active.zones?.main || [], sidebar: active.zones?.sidebar || [] });
            setDirty(false);
        }
    }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const save = useMutation({
        mutationFn: async () => (await rhm.patch(`/dashlayout/layouts/${active.id}`, { zones })).data,
        onSuccess: () => { toast.success("Dashboard layout saved"); setDirty(false); qc.invalidateQueries({ queryKey: ["dash-layouts"] }); },
        onError: (e) => toast.error(apiError(e)),
    });
    const patchLayout = useMutation({
        mutationFn: async ({ id, body }) => (await rhm.patch(`/dashlayout/layouts/${id}`, body)).data,
        onSuccess: () => qc.invalidateQueries({ queryKey: ["dash-layouts"] }),
        onError: (e) => toast.error(apiError(e)),
    });
    const createLayout = useMutation({
        mutationFn: async (body) => (await rhm.post("/dashlayout/layouts", body)).data,
        onSuccess: (row) => {
            toast.success("Segment layout created");
            setCreating(false);
            setActiveId(row.id);
            qc.invalidateQueries({ queryKey: ["dash-layouts"] });
        },
        onError: (e) => toast.error(apiError(e)),
    });
    const removeLayout = useMutation({
        mutationFn: async (id) => (await rhm.delete(`/dashlayout/layouts/${id}`)).data,
        onSuccess: () => { toast.success("Segment removed"); setActiveId(""); qc.invalidateQueries({ queryKey: ["dash-layouts"] }); },
        onError: (e) => toast.error(apiError(e)),
    });

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const zoneOf = (id) => (zones.main.some((t) => t.id === id) ? "main" : "sidebar");

    const onDragEnd = ({ active: a, over }) => {
        if (!over || a.id === over.id) return;
        const from = zoneOf(a.id);
        const to = zones.main.some((t) => t.id === over.id) ? "main" : "sidebar";
        setZones((prev) => {
            if (from === to) {
                const list = prev[from];
                return { ...prev, [from]: arrayMove(list, list.findIndex((t) => t.id === a.id),
                    list.findIndex((t) => t.id === over.id)) };
            }
            const tile = prev[from].find((t) => t.id === a.id);
            const target = [...prev[to]];
            target.splice(prev[to].findIndex((t) => t.id === over.id), 0, tile);
            return { ...prev, [from]: prev[from].filter((t) => t.id !== a.id), [to]: target };
        });
        setDirty(true);
    };

    const update = (id, patch) => {
        setZones((prev) => ({
            main: prev.main.map((t) => (t.id === id ? { ...t, ...patch } : t)),
            sidebar: prev.sidebar.map((t) => (t.id === id ? { ...t, ...patch } : t)),
        }));
        setDirty(true);
    };
    const move = (id) => {
        const from = zoneOf(id);
        const to = from === "main" ? "sidebar" : "main";
        setZones((prev) => ({
            ...prev,
            [from]: prev[from].filter((t) => t.id !== id),
            [to]: [...prev[to], prev[from].find((t) => t.id === id)],
        }));
        setDirty(true);
    };
    const remove = (id) => {
        setZones((prev) => ({ main: prev.main.filter((t) => t.id !== id),
            sidebar: prev.sidebar.filter((t) => t.id !== id) }));
        setDirty(true);
    };
    const add = (type, zone) => {
        setZones((prev) => ({ ...prev, [zone]: [...prev[zone], newTile(type)] }));
        setAdding(false);
        setDirty(true);
    };

    if (isLoading) return <div className="mono text-xs text-zinc-500">LOADING…</div>;

    return (
        <div data-testid="rhm-dashboard-layout-page">
            <PageHead title="Dashboard Layout" sub="Arrange what every customer sees on their account page."
                action={<div className="flex items-center gap-2">
                    <a href={`/account?layout=${active?.id || ""}`} target="_blank" rel="noreferrer"
                        className="mono text-[10px] tracking-[0.16em] text-zinc-400 hover:text-gold"
                        data-testid="dash-preview-as">PREVIEW AS →</a>
                    {editable && (
                        <Btn onClick={() => save.mutate()} disabled={!dirty || save.isPending}
                            data-testid="dash-save">{dirty ? "SAVE LAYOUT" : "SAVED"}</Btn>
                    )}
                </div>} />

            <div className="grid lg:grid-cols-4 gap-5">
                <Card title="Layouts" data-testid="dash-layout-list"
                    action={editable && <Btn variant="ghost" onClick={() => setCreating(true)} data-testid="dash-new-segment">
                        <Plus className="w-3.5 h-3.5" /></Btn>}>
                    <div className="space-y-2">
                        {layouts.map((l) => (
                            <button key={l.id} onClick={() => setActiveId(l.id)}
                                data-testid={`dash-layout-${l.id}`}
                                className={`w-full text-left px-3 py-2 border transition-colors ${
                                    l.id === active?.id ? "border-gold/60 bg-gold/10" : "border-white/10 hover:border-white/25"}`}>
                                <div className="text-sm">{l.name}</div>
                                <div className="mono text-[9px] tracking-[0.14em] text-zinc-500 mt-1">
                                    {l.kind === "default" ? "ALL CUSTOMERS"
                                        : `${(SEGMENT_LABELS[l.segment?.kind] || "").toUpperCase()} · PRIORITY ${l.priority}`}
                                </div>
                            </button>
                        ))}
                    </div>
                    {active?.kind === "segment" && editable && (
                        <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                            <Field label="Priority (lower wins)">
                                <Input type="number" value={active.priority} data-testid="dash-priority"
                                    onChange={(e) => patchLayout.mutate({ id: active.id, body: { priority: Number(e.target.value) } })} />
                            </Field>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={active.enabled !== false} data-testid="dash-enabled"
                                    onChange={(e) => patchLayout.mutate({ id: active.id, body: { enabled: e.target.checked } })} />
                                Segment active
                            </label>
                            <Btn variant="danger" onClick={() => removeLayout.mutate(active.id)} data-testid="dash-delete-segment">
                                <Trash2 className="w-3.5 h-3.5" /> DELETE SEGMENT
                            </Btn>
                        </div>
                    )}
                </Card>

                <div className="lg:col-span-3">
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                        <div className="grid md:grid-cols-5 gap-5">
                            <Zone name="main" label="Main column" tiles={zones.main} span="md:col-span-3"
                                onEdit={setEditing} onMove={move} onRemove={remove} onToggle={update}
                                editable={editable} onAdd={() => setAdding("main")} />
                            <Zone name="sidebar" label="Sidebar" tiles={zones.sidebar} span="md:col-span-2"
                                onEdit={setEditing} onMove={move} onRemove={remove} onToggle={update}
                                editable={editable} onAdd={() => setAdding("sidebar")} />
                        </div>
                    </DndContext>
                </div>
            </div>

            {editing && (
                <TileEditor tile={zones.main.concat(zones.sidebar).find((t) => t.id === editing)}
                    products={products} onChange={(patch) => update(editing, patch)}
                    onClose={() => setEditing(null)} />
            )}

            {adding && (
                <Modal open title="Add a tile" onClose={() => setAdding(false)} testId="dash-add-tile-modal">
                    <div className="grid grid-cols-2 gap-2">
                        {NEW_TILE_TYPES.map((t) => (
                            <button key={t} onClick={() => add(t, adding)} data-testid={`dash-add-${t}`}
                                className="border border-white/10 px-3 py-2 text-sm text-left hover:border-gold/60">
                                {TILE_LABELS[t]}
                            </button>
                        ))}
                    </div>
                </Modal>
            )}

            {creating && (
                <NewSegment products={products} onClose={() => setCreating(false)}
                    onCreate={(body) => createLayout.mutate(body)} />
            )}
        </div>
    );
}


const Zone = ({ name, label, tiles, span, onEdit, onMove, onRemove, onToggle, editable, onAdd }) => (
    <div className={span}>
        <div className="flex items-center justify-between mb-2">
            <div className="mono text-[10px] tracking-[0.18em] text-zinc-500">{label.toUpperCase()}</div>
            {editable && <Btn variant="ghost" onClick={onAdd} data-testid={`dash-add-tile-${name}`}>
                <Plus className="w-3.5 h-3.5" /> ADD TILE</Btn>}
        </div>
        <SortableContext items={tiles.map((t) => t.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2 min-h-[120px] border border-dashed border-white/10 p-2"
                data-testid={`dash-zone-${name}`}>
                {tiles.length === 0 && <Empty msg="Drop tiles here." />}
                {tiles.map((tile) => (
                    <TileCard key={tile.id} tile={tile} onEdit={onEdit} onMove={onMove}
                        onRemove={onRemove} onToggle={onToggle} editable={editable} />
                ))}
            </div>
        </SortableContext>
    </div>
);


const TileCard = ({ tile, onEdit, onMove, onRemove, onToggle, editable }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tile.id });
    return (
        <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}
            data-testid={`dash-tile-${tile.type}`}
            className={`bg-bg1 border border-white/10 px-3 py-2 flex items-center gap-2 ${isDragging ? "opacity-60" : ""}`}>
            <button {...attributes} {...listeners} className="text-zinc-600 hover:text-white cursor-grab"
                data-testid={`dash-drag-${tile.id}`}><GripVertical className="w-4 h-4" /></button>
            <button onClick={() => onEdit(tile.id)} className="flex-1 text-left" data-testid={`dash-edit-${tile.type}`}>
                <div className="text-sm">{tile.heading || TILE_LABELS[tile.type]}</div>
                <div className="mono text-[9px] tracking-[0.14em] text-zinc-500">{TILE_LABELS[tile.type]}</div>
            </button>
            {!tile.visible && <Badge tone="muted">HIDDEN</Badge>}
            {editable && (
                <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => onToggle(tile.id, { visible: !tile.visible })}
                        data-testid={`dash-visible-${tile.type}`} className="p-1 text-zinc-500 hover:text-white">
                        {tile.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => onMove(tile.id)} title="Move to the other column"
                        data-testid={`dash-move-${tile.type}`} className="p-1 text-zinc-500 hover:text-white">
                        <ArrowLeftRight className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => onRemove(tile.id)} data-testid={`dash-remove-${tile.type}`}
                        className="p-1 text-zinc-500 hover:text-red-400">
                        <Trash2 className="w-3.5 h-3.5" />
                    </button>
                </div>
            )}
        </div>
    );
};


const Color = ({ label, value, onChange, testId }) => (
    <Field label={label}>
        <div className="flex items-center gap-2">
            <input type="color" value={value || "#111111"} onChange={(e) => onChange(e.target.value)}
                data-testid={testId} className="h-9 w-12 bg-transparent border border-white/10" />
            <Input value={value || ""} placeholder="default" onChange={(e) => onChange(e.target.value)} />
            {value && <Btn variant="ghost" onClick={() => onChange("")}>CLEAR</Btn>}
        </div>
    </Field>
);


const TileEditor = ({ tile, products, onChange, onClose }) => {
    if (!tile) return null;
    const btn = tile.button || {};
    const setBtn = (patch) => onChange({ button: { ...btn, ...patch } });
    return (
        <Modal open title={`${TILE_LABELS[tile.type]} tile`} onClose={onClose} testId="dash-tile-editor" wide>
            <div className="grid md:grid-cols-2 gap-4">
                <Field label="Heading">
                    <Input value={tile.heading || ""} data-testid="tile-heading"
                        onChange={(e) => onChange({ heading: e.target.value })} />
                </Field>
                <Field label="Body text">
                    <Textarea rows={2} value={tile.body || ""} data-testid="tile-body"
                        onChange={(e) => onChange({ body: e.target.value })} />
                </Field>
                <Color label="Tile background" value={tile.bg} testId="tile-bg"
                    onChange={(v) => onChange({ bg: v })} />
                <Color label="Heading colour" value={tile.heading_color} testId="tile-heading-color"
                    onChange={(v) => onChange({ heading_color: v })} />
                <Color label="Body colour" value={tile.body_color} testId="tile-body-color"
                    onChange={(v) => onChange({ body_color: v })} />

                {tile.type === "image" && (
                    <>
                        <Field label="Image URL">
                            <Input value={tile.image_url || ""} data-testid="tile-image-url"
                                onChange={(e) => onChange({ image_url: e.target.value })} />
                        </Field>
                        <Field label="Image links to">
                            <Input value={tile.link || ""} placeholder="/shop"
                                onChange={(e) => onChange({ link: e.target.value })} />
                        </Field>
                    </>
                )}

                {["products", "books", "blog"].includes(tile.type) && (
                    <Field label="How many to show">
                        <Input type="number" min="1" max="6" value={tile.limit || 3} data-testid="tile-limit"
                            onChange={(e) => onChange({ limit: Number(e.target.value) })} />
                    </Field>
                )}

                {["products", "books"].includes(tile.type) && (
                    <Field label="Pick specific products (optional)" className="md:col-span-2">
                        <select multiple value={tile.product_ids || []} data-testid="tile-products"
                            onChange={(e) => onChange({
                                product_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}
                            className="w-full h-28 bg-bg0 border border-white/10 px-3 py-2 text-sm">
                            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </Field>
                )}

                <div className="md:col-span-2 border-t border-white/10 pt-4">
                    <div className="mono text-[10px] tracking-[0.18em] text-zinc-500 mb-3">BUTTON (OPTIONAL)</div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Label">
                            <Input value={btn.label || ""} data-testid="tile-button-label"
                                onChange={(e) => setBtn({ label: e.target.value })} />
                        </Field>
                        <Field label="Link">
                            <Input value={btn.href || ""} placeholder="/shop" data-testid="tile-button-href"
                                onChange={(e) => setBtn({ href: e.target.value })} />
                        </Field>
                        <Color label="Button background" value={btn.bg} testId="tile-button-bg"
                            onChange={(v) => setBtn({ bg: v })} />
                        <Color label="Button text colour" value={btn.color} testId="tile-button-color"
                            onChange={(v) => setBtn({ color: v })} />
                    </div>
                </div>
            </div>
            <div className="mt-5 flex justify-end">
                <Btn onClick={onClose} data-testid="tile-editor-done">DONE</Btn>
            </div>
        </Modal>
    );
};


const NewSegment = ({ products, onClose, onCreate }) => {
    const [form, setForm] = useState({ name: "", segment_kind: "consultation_buyers",
        product_id: "", tag: "", priority: 10 });
    return (
        <Modal open title="New segment layout" onClose={onClose} testId="dash-new-segment-modal">
            <div className="space-y-4">
                <Field label="Name">
                    <Input value={form.name} data-testid="segment-name"
                        onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </Field>
                <Field label="Applies to">
                    <Select value={form.segment_kind} data-testid="segment-kind"
                        onChange={(e) => setForm({ ...form, segment_kind: e.target.value })}>
                        {Object.entries(SEGMENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </Select>
                </Field>
                {form.segment_kind === "product_buyers" && (
                    <Field label="Product">
                        <Select value={form.product_id} data-testid="segment-product"
                            onChange={(e) => setForm({ ...form, product_id: e.target.value })}>
                            <option value="">Select a product…</option>
                            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </Select>
                    </Field>
                )}
                {form.segment_kind === "tag" && (
                    <Field label="Customer tag">
                        <Input value={form.tag} placeholder="vip" data-testid="segment-tag"
                            onChange={(e) => setForm({ ...form, tag: e.target.value })} />
                    </Field>
                )}
                <Field label="Priority (lower wins when a customer matches more than one)">
                    <Input type="number" value={form.priority} data-testid="segment-priority"
                        onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })} />
                </Field>
                <Btn onClick={() => onCreate(form)} disabled={!form.name.trim()} data-testid="segment-create">
                    CREATE SEGMENT
                </Btn>
            </div>
        </Modal>
    );
};
